import {ServiceBase} from "../ServiceBase";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {
    GeoLocationReason,
    GeoValidationResult,
    IGeoLocationStrategy,
    IGeoRunResult
} from "./strategies/geo-location.strategy.interface";
import {GeoLocationPluginStrategy} from "./strategies/plugin/geo-location-plugin.strategy";
import {GeoLocationRunnerInCasino} from "./geo-runner/geo-location-runner-in-casino";
import {IGeoLocationService} from "./geo-location.service.interface";
import {IGeoRestrictionsUpdateEventParams, IPeriodicGeoUpdateEventParams} from "../sdk/sdk-events";
import {isGeoComplyPluginInstallationRequired} from "./strategies/geo-comply-error-codes";
import {delay} from "../../../utils/delay";
import {TimeSpan} from "@sparkware/app-utils";
import {GeoLocationNativeStrategy} from "./strategies/native/geo-location-native.strategy";
import {IGeoLocationRunner} from "./geo-runner/geo-location-runner.interface";
import {GeoLocationRunnerInOtherBrands} from "./geo-runner/geo-location-runner-in-other-brands";
import {GeoAnalyticsContext} from "./geo-location-analytics";
import {isValidGeoKickEvent} from "./geo-runner/geo-kick-reasons";
import {LocalStorageKeys} from "../storage/local-storage-keys";
import {GeoLocationHtml5Strategy} from "./strategies/html5/geo-location-html5.strategy";


interface IGeoLocationParamsFromLogin {
    isGeoLocationInProcess: boolean;
    licenseKey: string;
    licenceWebKey: string;
    locationTransactionId: string;
    mobileNumber: string;
}

export class GeoLocationService extends ServiceBase implements IGeoLocationService {
    constructor(services: IServiceFactoryExtended) {
        super(services);
        if(services.configuration.servicesConfigs.GeoLocation.enable) {
            this._geoLocationStrategy = this._createGeoLocationStrategy();
            this._subscribeToSystemEvents();
            this._startUserIpDetectionTimer();

            this.reaction(() => this.services.user.isAuthorized, async (isAuthorized) => {
                this._geoLocationStrategy = this._createGeoLocationStrategy();
                this.services.localStorage.removeItem(LocalStorageKeys.clientIpAddresses);
                if(isAuthorized) {
                    this._startUserIpDetectionTimer();
                    await this._performLoginGeoLocation();
                } else {
                    this._stopUserIpDetectionTimer();
                }
            }, {
                //Don't put it on true. We don't want to run geo on every page refresh.
                fireImmediately: false
            });
        }
    }

    private _geoLocationStrategy: IGeoLocationStrategy | null = null;
    private _currentGeoRunner: IGeoLocationRunner | null = null;


    async isUserBlockedByGeo(): Promise<boolean> {
        await this.services.playerMessages.waitForPlayerMessagesReady();
        //We rely on the fact that if there are unrestricted games then it means we are not blocked by geo.
        //However, this is a superficial approach, and it should be changed when we will move the restrictions from SDK to Orbit.
        //We should base our decision on the restrictions reason.
        return !Object.values(this.services.games.gamesMap).some(gameInfo => gameInfo.restriction.isGameAllowedToPlayReal());
    }

    private _subscribeToSystemEvents(): void {
        this.services.sdk.events.onGeoRestrictionsUpdate(this._geoRestrictionsUpdateHandler);
        this.services.sdk.events.onGeoPeriodicUpdate(this._periodicGeoUpdateHandler);
    }



    private _userIpDetectionTimer: any = null;
    private _startUserIpDetectionTimer(): void {
        if(this.services.user.isAuthorized) {
            if(this._userIpDetectionTimer) {
                this._stopUserIpDetectionTimer();
            }
            this._userIpDetectionTimer = setInterval(this._detectUserIpChange, TimeSpan.parse(this.services.configuration.servicesConfigs.GeoLocation.ipDetectionInterval).totalMilliseconds);
        }
    }

    private _stopUserIpDetectionTimer(): void {
        if(this._userIpDetectionTimer) {
            clearInterval(this._userIpDetectionTimer);
            this._userIpDetectionTimer = null;
        }
    }

    private _detectUserIpChange = async () => {
        await this._createGeoLocationRunner(GeoAnalyticsContext.IpChangeDetection).runClientIpDetection();
    }

    private _createGeoLocationStrategy(): IGeoLocationStrategy | null {
        if(this._geoLocationStrategy) {
            this._geoLocationStrategy.dispose();
        }

        if(!this.services.user.isAuthorized) {
            return null;
        }

        const device = this.services.device;
        if(this.services.miniOrbit.isEnabled) {
            return null;
        } else if(device.hybrid.inCasino) {
            return new GeoLocationNativeStrategy(this.services);
        } else if(device.hybrid.isHybrid) {
            return null; //for any hybrid flavour that is not inCasino we don't do geolocation
        } else if(this.services.configuration.servicesConfigs.GeoLocation.Html5.enabled) {
            return new GeoLocationHtml5Strategy(this.services);
        } else if(device.os.isPC) {
            return new GeoLocationPluginStrategy(this.services);
        }

        return null;
    }
    private _createGeoLocationRunner(analyticsContext: GeoAnalyticsContext): IGeoLocationRunner {
        if(!this.services.user.isAuthorized) {
            throw new Error('_createGeoLocationRunner should never be called for anonymous user');
        }

        if(this._geoLocationStrategy) {
            return new GeoLocationRunnerInCasino(this._geoLocationStrategy, analyticsContext, this.services);
        } else {
            return new GeoLocationRunnerInOtherBrands(this.services, analyticsContext);
        }

    }

    private async _showGenericKickMessage(geoRunner: IGeoLocationRunner, geoRunResult: IGeoRunResult): Promise<void> {
        if(geoRunResult.validationResult !== GeoValidationResult.Pass
            && !isValidGeoKickEvent(geoRunResult.kickParams)
            && !isGeoComplyPluginInstallationRequired(geoRunResult.geoComplyFailure?.geoComplyError?.errorCode)) {
            //If the geo fail but there wasn't a kick reason we just show a generic geo fail message.
            //If there would be a kick reason then we don't show any message because any kick message will be handled by the geo flow itself
            await geoRunner.showGenericGeoKickMessage();
        }
    }

    private _periodicGeoUpdateHandler = async (params: IPeriodicGeoUpdateEventParams) => {
        if(this._currentGeoRunner) {
            const geoResult = await this._currentGeoRunner.waitForGeo();
            //If the current in progress geo fails there is no reason to continue with this periodic geo.
            //If we continued then the outcome would be that we will show again any geo popup that was already shown on the in progress failed geo flow
            if(geoResult.validationResult === GeoValidationResult.Fail) {
                return;
            }
        }

        if(!this.services.user.isAuthorized) {
            //Just in case there was a kick while waiting for current geo
            return;
        }

        try {
            this._currentGeoRunner = this._createGeoLocationRunner(GeoAnalyticsContext.PeriodicUpdate);
            const geoRunResult = await this._currentGeoRunner.runPeriodicGeo({
                geoStrategyRunParams: {
                    locationTransactionId: params.locationTransactionId,
                    licenseKey: params.licenseKey,
                    licenseWebKey: params.licenseWebKey,
                    mobileNumber: params.mobileNumber
                }
            });

            if(geoRunResult.validationResult !== GeoValidationResult.Pass) {
                await this.services.gameRunner.currentRunningGame?.closeGame({
                    shouldSuggestNextGame: false
                });

                this._showGenericKickMessage(this._currentGeoRunner, geoRunResult); //no need to await for this one
            }

        } finally {
            this._currentGeoRunner = null;
        }
    }

    private _geoRestrictionsUpdateHandler = async (params: IGeoRestrictionsUpdateEventParams) => {

        if(this._currentGeoRunner) {
            //if there is already a geo in progress then that one will handle the restrictions update and any kick reason
            return;
        }

        if(!isValidGeoKickEvent(params)) { //It means there is no kick. Just an update restriction which most probably also means that it was a successful geo
            return;
        }

        try {
            this._currentGeoRunner = this._createGeoLocationRunner(GeoAnalyticsContext.GeoKick);
            const geoRunResult = await this._currentGeoRunner.geoKick(params);
            if(geoRunResult.validationResult !== GeoValidationResult.Pass) {
                await this.services.gameRunner.currentRunningGame?.closeGame({
                    shouldSuggestNextGame: false
                });
            }

        } finally {
            this._currentGeoRunner = null;
        }
    }

    private _getGeoParamsFromLogin(): IGeoLocationParamsFromLogin | null {
        const geoLocationJson =  this.services.sdk.extractTokenProperty('geoLocation');
        if(!geoLocationJson) {
            return null;
        }

        return JSON.parse(geoLocationJson);
    }

    private _clearGeoParamsFromLogin(geoRunResult: IGeoRunResult): void {
        //We clear geo params received on login in order not to be use again since a geo transactionId can be used only once.
        //However we don't clear them if the reason geolocation wasn't successful was because missing GeoComply plugin.
        //A missing GeoComply plugin means that the geolocation wasn't run at all so these parameters can be reused for another geo.
        if(!isGeoComplyPluginInstallationRequired(geoRunResult.geoComplyFailure?.geoComplyError?.errorCode)) {
            this.services.sdk.updateTokenProperty('geoLocation', null);
        }
    }

    private async _performLoginGeoLocation(): Promise<void> {
        const geoLoginParams = this._getGeoParamsFromLogin();
        if(!geoLoginParams) {
            this.services.logger.warning('No geo location params returned from login');
            return;
        }

        if(!geoLoginParams.isGeoLocationInProcess) {
            return;
        }

        try {
            this._currentGeoRunner = this._createGeoLocationRunner(GeoAnalyticsContext.Login);

            const geoRunResult = await this._currentGeoRunner.loginGeoLocation({
                geoStrategyRunParams: {
                    locationTransactionId: geoLoginParams.locationTransactionId,
                    licenseKey: geoLoginParams.licenseKey,
                    licenseWebKey: geoLoginParams.licenceWebKey,
                    mobileNumber: geoLoginParams.mobileNumber,
                    reason: GeoLocationReason.Login
                },

            });

            if(geoRunResult.validationResult !== GeoValidationResult.Pass) {
                this._showGenericKickMessage(this._currentGeoRunner, geoRunResult); //no need to await for this one
            }


            this._clearGeoParamsFromLogin(geoRunResult);

        } catch(err) {
            throw err;
        } finally {
            this._currentGeoRunner = null;
        }
    }

    public async performGeoLocationOnOpenRealGame(gameType: number): Promise<GeoValidationResult> {

        let geoRunResult = await this._waitForCurrentGeo();

        if(geoRunResult) {
            return geoRunResult.validationResult;
        }

        const showGameRestrictionPopup = (geoRunner: IGeoLocationRunner, geoRunResult: IGeoRunResult) => {
            if(geoRunResult.validationResult !== GeoValidationResult.Pass && !isValidGeoKickEvent(geoRunResult.kickParams)) {
                //If the failure has a kick reason then it means we already shown a popup so there is no point to show another one now.
                geoRunner.showGameRestrictedPopup(geoRunResult);
            }

        }

        const loginGeoParams = this._getGeoParamsFromLogin();
        if(loginGeoParams?.isGeoLocationInProcess) {
            //If geo login params are still available it means that the geo login flow didn't finish most probably because user didn't install GeoComply plugin
            //then we can use those parameters to perform here the geolocation
            try {
                this._currentGeoRunner = this._createGeoLocationRunner(GeoAnalyticsContext.OpenGame);

                geoRunResult = await this._currentGeoRunner.openGameGeoLocation({
                    gameType: gameType,
                    geoStrategyRunParams: {
                        locationTransactionId: loginGeoParams.locationTransactionId,
                        licenseKey: loginGeoParams.licenseKey,
                        licenseWebKey: loginGeoParams.licenceWebKey,
                        mobileNumber: loginGeoParams.mobileNumber,
                        reason: GeoLocationReason.OpenRealGame
                    }
                });

                this._clearGeoParamsFromLogin(geoRunResult);
                showGameRestrictionPopup(this._currentGeoRunner, geoRunResult);

                return geoRunResult.validationResult;

            } finally {
                this._currentGeoRunner = null;
            }
        }

        //If the user is restricted by geo then we attempt a retry.
        if(await this.isUserBlockedByGeo()) {
            this._currentGeoRunner = this._createGeoLocationRunner(GeoAnalyticsContext.OpenGameUserBlocked);
            try {
                geoRunResult = await this._currentGeoRunner.geoRetryOnOpenRealGame({
                    showProgressIndicator: false,
                    gameType: gameType
                });
                showGameRestrictionPopup(this._currentGeoRunner, geoRunResult);

                return geoRunResult.validationResult;
            } finally {
                this._currentGeoRunner = null;
            }
        }

        //If we are not blocked by geo restrictions then we try to create a game token
        //We do this because it is possible that while the app was in sleep, platform to put geo restrictions on the current player
        //and if we were not there to get the firebase message then we'll think that the geo is fine but is not.
        //A call to getGameToken will return a specific error code if the user is restricted by geo
        try {
            await this.services.api.gameController.getGameToken(this._createGameTokenRequest(gameType));
        } catch (err) {
            if(err?.error_code === 100200) {
                //We only handle this error code here
                //On any other error code we consider that from Geo point of view all is good
                //and we will let the other game steps to deal with other errors (latter CallOpenRealGameApiGameStep will also make use of the getGameToken on the backend side)
                //For example there could be an error because of a pending action which is not relevant for the geo flow.
                this._currentGeoRunner = this._createGeoLocationRunner(GeoAnalyticsContext.OpenGameTokenFailed);
                try {
                    // Most probably because our app missed a periodic GEO while in sleep then platform put a restriction on the user since it didn't get a InformLocation from client.
                    // By doing a geo retry we might fix the user geo restrictions if the user is in the right location.
                    geoRunResult = await this._currentGeoRunner.geoRetryOnOpenRealGame({
                        showProgressIndicator: false,
                        gameType: gameType
                    });

                    showGameRestrictionPopup(this._currentGeoRunner, geoRunResult);

                    return geoRunResult.validationResult;
                } finally {
                    this._currentGeoRunner = null;
                }
            }
        }

        return GeoValidationResult.Pass;
    }

    private async _waitForCurrentGeo(): Promise<IGeoRunResult | null> {
        let geoRunResult: IGeoRunResult | null = null;
        while(this._currentGeoRunner) {
            //It means there is already a geo in progress. This can happen if the user click on a game when is not yet authorized
            //the login popup appears, and he performs login and immediately a geo flow will start but at the same time the game open flow will continue

            geoRunResult = await this._currentGeoRunner.waitForGeo();
            if(geoRunResult.validationResult === GeoValidationResult.Pass) {
                //If the current running goe is successful then we can return also here success.
                return geoRunResult;
            }

            //Here we wait a little bit in order to give the _geoRestrictionsUpdateHandler the chance to start a new geo if required (for example in case of geo kick with retry)
            await delay(TimeSpan.fromMilliseconds(500));
        }

        return geoRunResult;

    }

    private _createGameTokenRequest(gameType: number) {
        return {
            gameTypeId: gameType,
            isMobile: this.services.device.os.isMobileOS,
            regulationId:  this.services.regulation.current.regulationType, //this.serviceFactory.regulationService().getRegulationCode(),
            currencyCode: this.services.currency.currencyIso3, //this.serviceFactory.currencyService().getCurrency(),
            languageCode: this.services.language.currentLanguage, //this.serviceFactory.languageService().getISO3(),
            currentRoute: this.services.location.currentUri.pathName.toString(), // this.serviceFactory.windowService().getPathName(),
            isHybridEmbedded: this.services.device.hybrid.isEmbedded,
            productPackage: this.services.device.productPackageId, //this.serviceFactory.skinService().getProductPackageID()
            refreshToken: this.services.sdk.getRefreshToken()
        }
    }
}